import React from "react";
import { TriangleAlert, RotateCcw, Home, ChevronDown, ChevronUp } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("Uncaught Error in ErrorBoundary:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  toggleDetails = () => {
    this.setState((prevState) => ({ showDetails: !prevState.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center min-vh-100 p-4"
          style={{
            backgroundColor: "#f8f9fa",
            color: "#212529",
            fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          }}
        >
          <div
            className="card shadow-lg border-0 rounded-4 text-center p-4 p-md-5"
            style={{ maxWidth: "600px", width: "100%" }}
          >
            <div className="mb-4 d-flex justify-content-center">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "#fff2e8",
                  color: "#fa541c",
                }}
              >
                <TriangleAlert size={42} />
              </div>
            </div>

            <h2 className="fw-bold mb-2" style={{ color: "#1f2937" }}>
              Something Went Wrong
            </h2>
            <p className="text-secondary mb-4" style={{ fontSize: "0.95rem" }}>
              An unexpected error occurred in the application. We apologize for the inconvenience.
              Please try refreshing the page or navigating back to the home page.
            </p>

            <div className="d-flex flex-wrap align-items-center justify-content-center gap-3 mb-4">
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center px-4 py-2 rounded-3 text-white fw-semibold border-0"
                style={{ backgroundColor: "#fa8c16", cursor: "pointer" }}
                onClick={this.handleReload}
              >
                <RotateCcw size={18} className="me-2" />
                Reload Page
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary d-inline-flex align-items-center px-4 py-2 rounded-3 fw-semibold"
                onClick={this.handleGoHome}
              >
                <Home size={18} className="me-2" />
                Go to Home
              </button>
            </div>

            {this.state.error && (
              <div className="mt-3 border-top pt-3 text-start">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0 text-secondary d-flex align-items-center fs-7 fw-semibold border-0 bg-transparent"
                  onClick={this.toggleDetails}
                >
                  {this.state.showDetails ? (
                    <>
                      <ChevronUp size={16} className="me-1" /> Hide Technical Details
                    </>
                  ) : (
                    <>
                      <ChevronDown size={16} className="me-1" /> Show Technical Details
                    </>
                  )}
                </button>

                {this.state.showDetails && (
                  <div
                    className="mt-3 p-3 bg-dark text-light rounded-3 overflow-auto"
                    style={{ maxHeight: "220px", fontSize: "0.825rem", fontFamily: "monospace" }}
                  >
                    <p className="text-danger fw-bold mb-2">
                      {this.state.error.toString()}
                    </p>
                    {this.state.errorInfo?.componentStack && (
                      <pre className="m-0 text-white-50" style={{ whiteSpace: "pre-wrap" }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
